import { css } from '@emotion/react'
import api from 'api'
import { catmatAcitretina, catmatIsotretinoina, catmatTalidomida, catmatTretinoina } from 'api/ImpressaoMedicamentos'
import { Checkbox, isEmpty, Text } from 'bold-ui'
import { Box } from 'components/Box'
import { FieldArray } from 'components/form'
import { resolveName, usePecField } from 'components/form/core/hooks/useField'
import theme from 'config/theme'
import { differenceInYears } from 'date-fns'
import { IdentidadeGeneroEnum, SexoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useServerTime } from 'hooks/useServerTime'
import { groupBy } from 'lodash'
import React, { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  TermosOutrosMedicamentosImpressaoPrescricao,
  termosPrescricaoImpressao,
  TermosTalidomidaImpressaoPrescricao,
} from 'types/enums'
import { metaPath } from 'util/metaPath'
import { parseNumber } from 'util/number'

import { MedicamentoFormModel, MedicamentoSubtitle } from '../../model-prescricao'
import { PrescricaoMedicamentoPlanoModel } from '../../PrescricaoMedicamentoView'
import { convertMedicamentoToPrintDto } from '../../utils/converter-prescricao'
import { hasIsotretinoinaTretinoinaAcitretina } from '../../utils/utils-prescricao'
import { EncaminharPrescricaoMedicamentoItem } from '../components/EncaminharPrescricaoMedicamentoItem'
import TermosToPrintList from '../components/TermosToPrintList'
import { EncaminhamentoPrescricaoPrimaryButtonConfig } from '../EncaminhamentoPrescricaoModal'
import { PrescricaoDigitalFormModel } from '../prescricao-digital/model-prescricaoDigital'
import {
  ImpressaoMedicamentosInput,
  ImpressaoTermosDto,
  ReimpressaoReceitaMedicamentosDto,
} from './ImpressaoMedicamentosInput'

const path = metaPath<PrescricaoMedicamentoPlanoModel>()

export interface ImpressaoPrescricaoViewProps {
  medicamentos: MedicamentoFormModel[]
  prescricaoDigital: PrescricaoDigitalFormModel
  atendimentoId: ID
  municipioId: ID
  cidadaoId: ID
  cidadaoDataNascimento: LocalDate
  cidadaoSexo: SexoEnum
  isReimpressao: boolean
  cidadaoIdentidadeGenero: IdentidadeGeneroEnum
  setPrimaryButtonConfig: Dispatch<SetStateAction<EncaminhamentoPrescricaoPrimaryButtonConfig>>
}

function ImpressaoPrescricaoViewInternal(props: Readonly<ImpressaoPrescricaoViewProps>) {
  const {
    medicamentos,
    prescricaoDigital,
    atendimentoId,
    municipioId,
    cidadaoId,
    cidadaoDataNascimento,
    cidadaoSexo,
    isReimpressao,
    cidadaoIdentidadeGenero,
    setPrimaryButtonConfig,
  } = props

  const alreadyPrintedPathName = resolveName(path.alreadyPrinted)

  const {
    input: { value: alreadyPrintedValue, onChange: updateAlreadyPrinted },
  } = usePecField({ name: path.alreadyPrinted, subscription: { value: true } })

  const [medicamentosToPrint, setMedicamentosToPrint] = useState<MedicamentoFormModel[]>(medicamentos)
  const [medicamentosSelected, setMedicamentosSelected] = useState<MedicamentoFormModel[]>(medicamentos)
  const [isLoading, setIsLoading] = useState(false)

  const [subtitleTermos, setSubtitleTermos] = useState('')
  const [termosSelected, setTermosSelected] = useState<ImpressaoTermosDto[]>([])

  const [allChecked, setAllChecked] = useState(true)
  const { analytics } = useFirebase()
  const { getServerTimeNow } = useServerTime()

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao({
    onError: () => {
      setIsLoading(false)
    },
  })

  const termosToPrint = useRef<ImpressaoTermosDto[]>([])

  const idadeCidadao = differenceInYears(getServerTimeNow(), new Date(cidadaoDataNascimento))

  const talidomidas = useMemo(
    () =>
      medicamentosToPrint.filter((medicamento) => medicamento.principioAtivoCombo?.codigoCatMat === catmatTalidomida),
    [medicamentosToPrint]
  )

  const hasTalidomida = useMemo(() => !isEmpty(talidomidas), [talidomidas])

  const noSelections = useMemo(() => isEmpty(medicamentosSelected) && isEmpty(termosSelected), [
    medicamentosSelected,
    termosSelected,
  ])

  const isAtendimento = !isEmpty(atendimentoId)

  const insertTermos = useCallback(() => {
    const termos: ImpressaoTermosDto[] = []
    const anexoV_A = termosPrescricaoImpressao[TermosTalidomidaImpressaoPrescricao.ANEXO_V_A]
    const anexoV_B = termosPrescricaoImpressao[TermosTalidomidaImpressaoPrescricao.ANEXO_V_B]

    const isCidadaoMaior55 = idadeCidadao >= 55
    const isCidadaoCis =
      cidadaoIdentidadeGenero === IdentidadeGeneroEnum.HOMEM_CIS ||
      cidadaoIdentidadeGenero === IdentidadeGeneroEnum.MULHER_CIS
    const isCidadaoIndeterminadoOrNotCis =
      cidadaoSexo === SexoEnum.INDETERMINADO || (cidadaoIdentidadeGenero && !isCidadaoCis)

    const hasAnexoVAOrXVI = isCidadaoIndeterminadoOrNotCis || cidadaoSexo === SexoEnum.MASCULINO || isCidadaoMaior55
    const hasAnexoVBOrXV = !isCidadaoMaior55 && (isCidadaoIndeterminadoOrNotCis || cidadaoSexo === SexoEnum.FEMININO)

    const filtroTalidomida = (termo: string) => {
      const isNotAnexoV = termo !== anexoV_A && termo !== anexoV_B
      const hasAnexoVA = termo === anexoV_A && hasAnexoVAOrXVI
      const hasAnexoVB = termo === anexoV_B && hasAnexoVBOrXV

      return isNotAnexoV || hasAnexoVA || hasAnexoVB
    }

    if (hasTalidomida) {
      talidomidas.forEach((talidomida) => {
        Object.keys(TermosTalidomidaImpressaoPrescricao)
          .filter((termo) => filtroTalidomida(termo))
          .forEach((key: TermosTalidomidaImpressaoPrescricao) =>
            termos.push({
              idMedicamentoReferencia: talidomida.id,
              receitaId: talidomida.editingId,
              termo: key,
              codigosCatmatRelacionados: [talidomida.principioAtivoCombo.codigoCatMat],
              principioAtivo: talidomida.principioAtivoCombo?.principioAtivo.nome,
              quantidade: talidomida.quantidade,
              duracaoTratamento: talidomida.duracao,
              unidadeMedidaDuracao: talidomida.escalaDuracao,
              dataInicioTratamento: talidomida.dataInicioTratamento,
            } as ImpressaoTermosDto)
          )
      })
    }

    if (hasIsotretinoinaTretinoinaAcitretina(medicamentosToPrint)) {
      const catmatTermosList = [...catmatAcitretina, ...catmatIsotretinoina, ...catmatTretinoina]
      const medicamentosComTermos = medicamentosToPrint.filter(
        (medicamento) =>
          !medicamento.registroManual && catmatTermosList.includes(medicamento.principioAtivoCombo.codigoCatMat)
      )

      if (!isEmpty(medicamentosComTermos)) {
        const medicamentosByReceitaId = groupBy(medicamentosComTermos, 'editingId')

        Object.keys(medicamentosByReceitaId).forEach((receitaId) => {
          const catmatRelacionados = medicamentosByReceitaId[receitaId].map(
            (medicamento) => medicamento.principioAtivoCombo.codigoCatMat
          )
          if (hasAnexoVBOrXV) {
            termos.push({
              termo: termosPrescricaoImpressao[TermosOutrosMedicamentosImpressaoPrescricao.ANEXO_XV],
              codigosCatmatRelacionados: catmatRelacionados,
              receitaId: parseNumber(receitaId) || '0',
            } as ImpressaoTermosDto)
          }
          if (hasAnexoVAOrXVI) {
            termos.push({
              termo: termosPrescricaoImpressao[TermosOutrosMedicamentosImpressaoPrescricao.ANEXO_XVI],
              codigosCatmatRelacionados: catmatRelacionados,
              receitaId: parseNumber(receitaId) || '0',
            } as ImpressaoTermosDto)
          }
        })
      }
    }

    setTermosSelected(termos)
    termosToPrint.current = termos
  }, [cidadaoIdentidadeGenero, cidadaoSexo, hasTalidomida, idadeCidadao, medicamentosToPrint, talidomidas])

  useEffect(() => {
    setMedicamentosToPrint(medicamentos)
  }, [medicamentos])

  useEffect(() => {
    const subtitleItems: string[] = []
    medicamentos.forEach((med) => {
      if (catmatAcitretina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push(MedicamentoSubtitle.ACITRETINA)
      } else if (catmatIsotretinoina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push(MedicamentoSubtitle.ISOTRETINOINA)
      } else if (catmatTretinoina.includes(med.principioAtivoCombo?.codigoCatMat)) {
        subtitleItems.push(MedicamentoSubtitle.TRETINOINA)
      }
    })
    setSubtitleTermos(subtitleItems.join(' / '))
    insertTermos()
  }, [medicamentos, insertTermos])

  const handleOnChangeMedicamentosSelected = (checked: boolean, medicamento: MedicamentoFormModel) => {
    if (checked) {
      setMedicamentosSelected([...medicamentosSelected, medicamento])
    } else {
      setMedicamentosSelected(medicamentosSelected.filter((med) => med.id !== medicamento.id))
      setAllChecked(false)
    }
  }

  const handleOnChangeTermosSelected = (checked: boolean, termoChecked: ImpressaoTermosDto) => {
    if (checked) {
      setTermosSelected([...termosSelected, termoChecked])
    } else {
      setTermosSelected(termosSelected.filter((termoSelected) => termoSelected.termo !== termoChecked.termo))
      setAllChecked(false)
    }
  }

  const handleOnChangeAllChecked = () => {
    const newState = !allChecked
    setAllChecked(newState)
    setMedicamentosSelected(newState ? medicamentos : [])
    setTermosSelected(newState ? termosToPrint.current : [])
  }

  const alreadyPrintedValueMemo = useMemo(() => alreadyPrintedValue, [alreadyPrintedValue])

  const findAlreadyPrintedElement = useCallback((id: ID) => alreadyPrintedValueMemo?.includes(id), [
    alreadyPrintedValueMemo,
  ])

  const updateAlreadyPrintedCallback = useCallback((input) => updateAlreadyPrinted(input), [updateAlreadyPrinted])

  const insertMedicamentosAndTermosImpressos = useCallback(() => {
    setIsLoading(false)
    const medicamentosPrinted = medicamentosSelected
      .filter((medicamento) => !findAlreadyPrintedElement(medicamento))
      .map((medicamento) => medicamento.id)
    const termosPrinted = termosSelected
      .filter((termo) => !findAlreadyPrintedElement(termo.termo))
      .map((termo) => termo.termo)
    const allPrinted = [...alreadyPrintedValueMemo, ...medicamentosPrinted, ...termosPrinted]
    updateAlreadyPrintedCallback(Array.from(new Set(allPrinted)))
  }, [
    medicamentosSelected,
    termosSelected,
    updateAlreadyPrintedCallback,
    findAlreadyPrintedElement,
    alreadyPrintedValueMemo,
  ])

  const handlePrintImpressao = useCallback(() => {
    setIsLoading(true)

    const input: ImpressaoMedicamentosInput = {
      atendimentoId,
      cidadaoDataNascimento: Date.parse(cidadaoDataNascimento),
      cidadaoSexo,
      localidadeId: municipioId,
      isotretinoinaTretinoinaAcitretinaSub: subtitleTermos ?? null,
      medicamentos: medicamentosSelected.map(convertMedicamentoToPrintDto),
      termos: termosSelected,
    }

    handleImpressaoPath({
      apiPath: api.prescricaoMedicamento.imprimirMedicamentos,
      values: input,
      file: 'Imprimir prescrição de medicamentos',
    })
      .then(() => {
        insertMedicamentosAndTermosImpressos()
      })
      .catch(() => {
        setIsLoading(false)
      })

    analytics.logEvent('click_imprimir_modal_impressao_prescricao')
  }, [
    analytics,
    atendimentoId,
    cidadaoDataNascimento,
    cidadaoSexo,
    handleImpressaoPath,
    insertMedicamentosAndTermosImpressos,
    medicamentosSelected,
    municipioId,
    subtitleTermos,
    termosSelected,
  ])

  const handlePrintReimpressao = useCallback(() => {
    setIsLoading(true)

    const input: ReimpressaoReceitaMedicamentosDto = {
      contextoId: isAtendimento ? atendimentoId : cidadaoId,
      receitasMedicamento: medicamentosSelected.map((medicamento) => medicamento.editingId),
      termos: termosSelected,
      isotretinoinaTretinoinaAcitretinaSub: subtitleTermos ?? null,
      isAtendimento: isAtendimento,
    }

    handleImpressaoPath({
      apiPath: api.prescricaoMedicamento.reimprimirMedicamentos,
      values: input,
      file: 'Reimprimir prescrição de medicamentos',
    })
      .then(() => {
        insertMedicamentosAndTermosImpressos()
      })
      .catch((error) => handleImpressaoError(error, 'Erro ao reimprimir prescrição de medicamentos.'))

    analytics.logEvent('click_imprimir_modal_reimpressao_prescricao')
  }, [
    analytics,
    atendimentoId,
    cidadaoId,
    handleImpressaoError,
    handleImpressaoPath,
    insertMedicamentosAndTermosImpressos,
    isAtendimento,
    medicamentosSelected,
    subtitleTermos,
    termosSelected,
  ])

  const primaryButtonConfigMemo = useMemo(
    () => ({
      text: 'Imprimir',
      tooltipText: noSelections && 'Selecione um medicamento para possibilitar a impressão',
      loading: isLoading,
      disabled: noSelections,
      onClick: isReimpressao ? handlePrintReimpressao : handlePrintImpressao,
    }),
    [isLoading, noSelections, isReimpressao, handlePrintReimpressao, handlePrintImpressao]
  )

  useEffect(() => setPrimaryButtonConfig(primaryButtonConfigMemo), [primaryButtonConfigMemo, setPrimaryButtonConfig])

  const renderFieldArray = () => {
    return (
      <Fragment>
        <Box style={styles.checkBox}>
          <Checkbox
            label='Marcar todos'
            checked={allChecked}
            onChange={handleOnChangeAllChecked}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        <EncaminharPrescricaoMedicamentoItem
          prescricaoDigital={prescricaoDigital}
          isReimpressao={isReimpressao}
          medicamentoIdsSelecionado={medicamentosSelected.map((med) => med.id)}
          medicamentosToPrint={medicamentosToPrint}
          onChange={handleOnChangeMedicamentosSelected}
        />
        {(hasTalidomida || hasIsotretinoinaTretinoinaAcitretina(medicamentosToPrint)) && (
          <Fragment>
            <Text
              css={css`
                margin-bottom: 0.5rem;
              `}
            >
              Selecione os termos que deseja imprimir
            </Text>

            <TermosToPrintList
              termos={termosSelected.map((term) => term.termo)}
              termosToPrint={termosToPrint.current}
              acitretinaIsotretinoinaTretinoinaSub={subtitleTermos}
              onClickCheckbox={handleOnChangeTermosSelected}
              findAlreadyPrintedElement={findAlreadyPrintedElement}
              isReimpressao={isReimpressao}
            />
          </Fragment>
        )}
      </Fragment>
    )
  }

  return <FieldArray<ID> name={alreadyPrintedPathName} render={renderFieldArray} />
}

export const ImpressaoPrescricaoView = React.memo(ImpressaoPrescricaoViewInternal)

const styles = {
  checkBox: css`
    border: 1px solid;
    border-radius: 2px;
    border-color: ${theme.pallete.gray.c80};
    padding: 0.5rem 0.75rem;
  `,
}
